import _isEmpty from 'lodash/isEmpty';

import { navigate } from '@reach/router';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';

import FormPost from 'components/common/form-saml-response/formPost';
import { CommonContextV2, CommonLayoutV2, CustomButtonV2, CustomCheckboxV2, CustomSnackbarV2 } from 'components/v2';

import { axios } from 'services/axios';
import {
    COMMON_ERROR_MESSAGE,
    COOKIE_ERROR_MESSAGE,
    errorMessageMap,
    NETWORK_ERROR_MESSAGE,
    OPSWAT_PRIVACY_POLICY_URL,
    OPSWAT_TERMS_OF_SERVICE_URL
} from 'services/Constant';
import { logService } from 'services/LogService';
import { default as consentData } from 'services/productData';
import { isWhiteListed } from 'services/utility';

import './consent.scss';

const pageTitle = 'OPSWAT Accounts | Consent';
const enableLogging = ['dev', 'qa', 'local', 'staging'].includes(process.env.REACT_APP_ENVIRONMENT);

const ConsentPageV2 = ({ location }) => {
    const { query } = useContext(CommonContextV2);

    const [loading, setLoading] = useState(false);
    const [consentAgree, setConsentAgree] = useState(false);

    const [errors, setErrors] = useState([]);
    const [postInput, setPostInput] = useState();
    const [entityEndpoint, setEntityEndpoint] = useState();

    const appName = useMemo(() => location.state?.appName, [location]);

    const onProceed = (e) => {
        e && e.preventDefault();

        enableLogging && console.log('📝 [CONSENT] User initiated consent agreement process');

        setLoading(true);
        (async () => {
            try {
                const requestPayload = {
                    isConsent: true,
                    appId: location.state.appId || 'appMyOPSWAT0001',
                    SAMLRequest: location.state.samlRequest,
                    RelayState: location.state.relayState
                };

                enableLogging &&
                    console.log('📤 [CONSENT-REQUEST] Sending consent agreement to backend', {
                        appId: requestPayload.appId,
                        hasSAMLRequest: !!requestPayload.SAMLRequest,
                        hasRelayState: !!requestPayload.RelayState
                    });

                const response = await axios.post('/consent-app-agree', requestPayload);
                console.log('[CONSENT-RESPONSE]', response);

                if (response.status === 200) {
                    if (!response.data?.loginResponse) {
                        if (query.redirect && isWhiteListed(query.redirect)) {
                            enableLogging &&
                                console.log(
                                    '🔀 [CONSENT-REDIRECT] Empty login response, redirecting to specified redirect URL',
                                    { redirect: query.redirect }
                                );
                            window.location.href = query.redirect;
                            return;
                        }
                        const redirectUrl = `${process.env.REACT_APP_app_href_OCM}/`;
                        enableLogging &&
                            console.log('🔀 [CONSENT-EMPTY] Empty login response, redirecting to My OPSWAT (default)', {
                                environment: process.env.REACT_APP_ENVIRONMENT,
                                redirectUrl
                            });
                        window.location.href = redirectUrl;
                        return;
                    }

                    enableLogging &&
                        console.log('✅ [CONSENT-SUCCESS] Consent agreement accepted successfully', {
                            hasContext: !!response.data.loginResponse.context,
                            hasRelayState: !!response.data.loginResponse.relayState,
                            entityEndpoint: response.data.loginResponse.entityEndpoint
                        });

                    const input = [{ name: 'SAMLResponse', value: response.data.loginResponse.context }];
                    if (response.data.loginResponse.relayState) {
                        enableLogging && console.log('🔗 [CONSENT-SAML] Adding RelayState to SAML response');
                        input.push({ name: 'RelayState', value: response.data.loginResponse.relayState });
                    }

                    enableLogging &&
                        console.log('📮 [CONSENT-POST] Preparing POST form for SAML redirect', {
                            inputCount: input.length,
                            endpoint: response.data.loginResponse.entityEndpoint
                        });

                    setPostInput(input);
                    setEntityEndpoint(response.data.loginResponse.entityEndpoint);
                }
            } catch (err) {
                logService.error(err);
                enableLogging && console.error('❌ [CONSENT-ERROR] Consent agreement failed', err);

                let title;
                const messages = [];
                if (
                    (err?.response?.status >= 400 && err?.response?.status < 500) ||
                    errorMessageMap(err?.response?.data?.error?.code)
                ) {
                    if (err?.response?.status === 413) {
                        enableLogging &&
                            console.error('🍪 [CONSENT-ERROR] Payload too large - cookie issue', {
                                status: err?.response?.status
                            });
                        title = 'Error';
                        messages.push(COOKIE_ERROR_MESSAGE);
                    } else {
                        enableLogging &&
                            console.error('⚠️ [CONSENT-ERROR] Client error occurred', {
                                status: err?.response?.status,
                                errorCode: err?.response?.data?.error?.code
                            });
                        title = 'Error';
                        messages.push(COMMON_ERROR_MESSAGE);
                    }
                } else {
                    enableLogging &&
                        console.error('🌐 [CONSENT-ERROR] Network or server error', {
                            status: err?.response?.status
                        });
                    title = 'Error';
                    messages.push(NETWORK_ERROR_MESSAGE);
                }

                const mappedError = errorMessageMap(err?.response?.data?.error?.code);
                if (mappedError) {
                    enableLogging &&
                        console.error('🗺️ [CONSENT-ERROR] Mapped error message', {
                            errorCode: err?.response?.data?.error?.code,
                            message: mappedError
                        });
                    messages.push(mappedError);
                }

                setErrors([
                    {
                        title,
                        error: err,
                        messages
                    }
                ]);
                setLoading(false);
            }
        })();
    };

    const formResponseDom = useMemo(
        () => <FormPost inputs={postInput} entityEndpoint={entityEndpoint} />,
        [postInput, entityEndpoint]
    );

    const consentForm = useMemo(() => {
        const primaryConsent = consentData[appName] || consentData.MyOPSWAT;

        return (
            <div className='consent__form'>
                <h3>Before continuing to {primaryConsent.shortname}, you must agree to:</h3>
                <CustomCheckboxV2
                    required
                    checked={!!consentAgree}
                    label={
                        <span>
                            I agree to the OPSWAT Inc.{' '}
                            <a href={OPSWAT_TERMS_OF_SERVICE_URL} target='_blank' rel='noopener noreferrer'>
                                Terms of Service
                            </a>{' '}
                            and{' '}
                            <a href={OPSWAT_PRIVACY_POLICY_URL} target='_blank' rel='noopener noreferrer'>
                                Privacy Policy
                            </a>
                            , unless my organization has a separate written agreement with OPSWAT Inc., in which case
                            those separate terms shall apply.
                        </span>
                    }
                    name={appName}
                    onChange={(evt) => setConsentAgree(evt.target.checked)}
                />
                <CustomButtonV2 onClick={onProceed} disabled={!consentAgree || loading} loading={loading}>
                    Proceed
                </CustomButtonV2>
                {!_isEmpty(errors) && (
                    <CustomSnackbarV2
                        messageList={errors}
                        styles={{
                            maxWidth: '100%'
                        }}
                    />
                )}
            </div>
        );
    }, [loading, consentAgree, appName, errors]);

    useEffect(() => {
        if (!location.state) {
            navigate('/login');
        }
    }, [location.state]);

    return (
        <CommonLayoutV2
            isLoading={false}
            pageTitle={pageTitle}
            contentStyles={{
                gap: '40px'
            }}
        >
            <div className='consent__container'>
                {consentForm}
                {formResponseDom}
            </div>
        </CommonLayoutV2>
    );
};

export default ConsentPageV2;

ConsentPageV2.propTypes = {
    location: PropTypes.object
};
